<template>
    <main class="organizations-page">
        <div class="organizations-workspace">
            <account-settings-header
                :items="links"
                title="Organizations"
                subtitle="Your people, branches, and shared access — together in one workspace."
                :icon="mdiOfficeBuildingOutline"
            />

            <div v-if="pageError" class="workspace-notice" role="alert">
                <div
                    ><h2>We couldn’t load your organizations</h2><p>{{ pageError }}</p></div
                >
                <button class="workspace-button" type="button" @click="retryLoad">Try again</button>
            </div>
            <div v-else-if="loading || $apollo.loading" class="workspace-notice" role="status" aria-live="polite">
                <v-progress-circular indeterminate color="#f0784c" size="24" width="2" />
                <p>Loading your organization workspace…</p>
            </div>
            <section v-else-if="noBranches" class="workspace-card empty-state">
                <h2>A workspace for your organization</h2>
                <p>No organization is available for this account. Need help getting connected?</p>
                <div class="inline-actions">
                    <router-link class="workspace-button" :to="{ name: 'home' }">Go home</router-link>
                    <router-link class="workspace-button secondary" to="/help-support">Get help</router-link>
                </div>
            </section>
            <template v-else-if="currentLevel.baseAccountId">
                <section
                    class="workspace-card organization-summary"
                    :class="{ 'with-branches': checkAdmin(currentLevel) && currentLevel.enterprise }"
                    aria-labelledby="organization-title"
                >
                    <nav
                        v-if="canGoToParent || currentLevel.baseAccountId !== baseLevelId"
                        class="organization-navigation"
                        aria-label="Organization hierarchy"
                    >
                        <span class="organization-navigation__label">ORGANIZATION PATH</span>
                        <div class="organization-navigation__trail">
                            <button
                                v-if="canGoToParent"
                                type="button"
                                class="organization-navigation__link"
                                @click="changeOrg(currentLevel.parentOrgId)"
                            >
                                <v-icon small>{{ mdiArrowLeft }}</v-icon>
                                <span
                                    ><small>BACK TO PARENT</small
                                    >{{ currentLevel.parentOrgName || 'Parent organization' }}</span
                                >
                            </button>
                            <span
                                v-if="canGoToParent && baseLevelId && currentLevel.baseAccountId !== baseLevelId"
                                class="organization-navigation__separator"
                                aria-hidden="true"
                            >
                                <v-icon small>{{ mdiChevronRight }}</v-icon>
                            </span>
                            <button
                                v-if="baseLevelId && currentLevel.baseAccountId !== baseLevelId"
                                type="button"
                                class="organization-navigation__link organization-navigation__link--home"
                                @click="changeOrg(baseLevelId)"
                            >
                                <v-icon small>{{ mdiHomeOutline }}</v-icon>
                                <span><small>ROOT ORGANIZATION</small>My organization</span>
                            </button>
                        </div>
                    </nav>
                    <div class="organization-identity">
                        <span class="eyebrow">ORGANIZATION OVERVIEW</span>
                        <div class="organization-title-row">
                            <h2 id="organization-title">{{ currentLevel.baseAccountName }}</h2>
                            <update-org
                                v-if="checkAdmin(currentLevel)"
                                class="organization-title-row__edit"
                                :current-level="currentLevel"
                                :table-view="true"
                                :delete-only="false"
                                :icon-only="true"
                                @updated="updateData"
                                @deleted="deletedOrg($event)"
                            />
                        </div>
                        <div v-if="currentLevel.address" class="organization-address"
                            ><print-org-address :address="currentLevel.address"
                        /></div>
                        <p v-else class="muted">No address added yet.</p>
                        <div class="identity-actions person-badges">
                            <span class="workspace-badge" :class="{ 'badge-admin': checkAdmin(currentLevel) }">{{
                                checkAdmin(currentLevel) ? 'Administrator' : 'Member'
                            }}</span>
                        </div>
                        <dl class="organization-counts" aria-label="Membership summary">
                            <div
                                ><dt>Joined</dt><dd>{{ memberCounts.joined }}</dd></div
                            >
                            <div
                                ><dt>Pending</dt><dd>{{ memberCounts.pending }}</dd></div
                            >
                            <div
                                ><dt>Disabled</dt><dd>{{ memberCounts.disabled }}</dd></div
                            >
                        </dl>
                    </div>

                    <section
                        v-if="checkAdmin(currentLevel) && currentLevel.enterprise"
                        class="branch-browser"
                        aria-labelledby="branches-title"
                    >
                        <div class="section-heading">
                            <div class="branch-heading-copy">
                                <span class="eyebrow">ORGANIZATION STRUCTURE</span>
                                <h2 id="branches-title">
                                    Branches <span class="muted">({{ currentLevel.childrenOrg.length }})</span>
                                </h2>
                            </div>
                            <div class="branch-create-action">
                                <create-sub-org
                                    :no-parent-org="false"
                                    :current-level="currentLevel"
                                    :table-view="true"
                                    :icon-only="true"
                                    @created="updateData"
                                />
                            </div>
                        </div>
                        <ul
                            v-if="currentLevel.childrenOrg.length"
                            class="branch-list"
                            aria-label="Organization branches"
                            tabindex="0"
                        >
                            <li v-for="branch in currentLevel.childrenOrg" :key="branch.orgId" class="branch-row">
                                <button
                                    type="button"
                                    class="branch-row__open"
                                    :aria-label="`View branch ${branch.organizationName}`"
                                    @click="changeOrg(branch.orgId)"
                                >
                                    <span class="branch-details">
                                        <strong>{{ branch.organizationName }}</strong>
                                        <span v-if="branch.address" class="organization-address">
                                            <print-org-address :address="branch.address" />
                                        </span>
                                        <span v-else class="muted">No address added yet.</span>
                                    </span>
                                    <span class="branch-row__action">View branch</span>
                                    <v-icon small aria-hidden="true">{{ mdiArrowRight }}</v-icon>
                                </button>
                            </li>
                        </ul>
                        <p v-else class="branch-empty"
                            >No branches yet. Create a branch to organize your teams and locations.</p
                        >
                    </section>
                </section>

                <section class="workspace-card" aria-labelledby="members-title">
                    <div class="section-heading">
                        <div
                            ><span class="eyebrow">PEOPLE &amp; PERMISSIONS</span><h2 id="members-title">Members</h2
                            ><p
                                >Joined accounts and invitations for this organization. Disabled accounts remain joined
                                members.</p
                            ></div
                        >
                        <add-user
                            v-if="checkAdmin(currentLevel)"
                            :current-level="currentLevel"
                            :table-view="true"
                            :icon-only="true"
                            @added="updateData"
                        />
                    </div>
                    <div class="list-controls">
                        <label class="search-field"
                            >Search members<input
                                v-model="searchUserAccounts"
                                type="search"
                                placeholder="Search by email"
                        /></label>
                        <label class="status-field"
                            >Account status<select v-model="memberStatus"
                                ><option>All</option
                                ><option>Members</option
                                ><option>Pending</option
                                ><option>Disabled</option></select
                            ></label
                        >
                        <span class="result-count" aria-live="polite">{{ filteredMembers.length }} results</span>
                    </div>
                    <ul v-if="filteredMembers.length" class="people-list" aria-label="Organization members">
                        <li
                            v-for="(item, index) in filteredMembers"
                            :key="item.accountId || item.email || index"
                            class="person-row"
                        >
                            <div class="person-summary">
                                <div class="person-identity">
                                    <span class="person-email">{{ item.email || 'Email unavailable' }}</span>
                                </div>
                                <div class="person-controls">
                                    <div class="person-badges">
                                        <span v-if="!item.account" class="workspace-badge badge-pending">Pending</span>
                                        <span
                                            v-if="item.account && item.isAccountLocked"
                                            class="workspace-badge badge-disabled"
                                            >Disabled</span
                                        >
                                        <span class="workspace-badge" :class="{ 'badge-admin': item.admin }">{{
                                            item.admin ? 'Admin' : 'Member'
                                        }}</span>
                                    </div>
                                    <button
                                        v-if="canManageMember(item)"
                                        :id="`member-manage-${index}`"
                                        type="button"
                                        class="manage-button"
                                        :aria-label="`Manage ${item.email || 'account'}`"
                                        :title="
                                            expandedMember === item
                                                ? 'Close member actions'
                                                : `Manage ${item.email || 'account'}`
                                        "
                                        :aria-expanded="expandedMember === item ? 'true' : 'false'"
                                        :aria-controls="`member-actions-${index}`"
                                        @click="expandedMember = expandedMember === item ? null : item"
                                    >
                                        <v-icon small aria-hidden="true">{{
                                            expandedMember === item ? mdiClose : mdiDotsVertical
                                        }}</v-icon>
                                    </button>
                                </div>
                            </div>
                            <transition name="member-actions">
                                <div
                                    v-if="canManageMember(item) && expandedMember === item"
                                    :id="`member-actions-${index}`"
                                    class="member-actions"
                                    role="group"
                                    :aria-labelledby="`member-manage-${index}`"
                                >
                                    <div v-if="checkTopOrg() && item.account" class="access-actions">
                                        <div class="action-copy">
                                            <h3>Global account access</h3>
                                            <p>{{
                                                item.isAccountLocked
                                                    ? 'Restore access to this account.'
                                                    : 'Disable access across the account, not just this organization.'
                                            }}</p>
                                        </div>
                                        <button
                                            type="button"
                                            class="workspace-button account-access-button"
                                            :disabled="accessBusy"
                                            :aria-label="`${
                                                item.isAccountLocked ? 'Enable' : 'Disable'
                                            } global access for ${item.email}`"
                                            @click="requestAccountAccess(item)"
                                            >{{ item.isAccountLocked ? 'Enable' : 'Disable' }}</button
                                        >
                                    </div>
                                    <div v-if="checkAdmin(currentLevel)" class="organization-actions">
                                        <div class="action-copy">
                                            <h3>{{ item.account ? 'Organization access' : 'Invitation' }}</h3>
                                            <p>{{
                                                !item.account
                                                    ? 'Revoke this pending invitation to the organization.'
                                                    : item.accountId === $auth.user.id
                                                    ? 'Your own role cannot be modified here.'
                                                    : 'Change the admin role or remove this member from the organization.'
                                            }}</p>
                                        </div>
                                        <update-user
                                            v-if="item.account"
                                            :current-level="currentLevel"
                                            :current-account="item"
                                            @updated="updateData($event)"
                                        />
                                        <remove-pending
                                            v-else
                                            :current-level="currentLevel"
                                            :current-account="item"
                                            :table-view="true"
                                            @deleted="updateData($event)"
                                        />
                                    </div>
                                </div>
                            </transition>
                        </li>
                    </ul>
                    <p v-else class="empty-state">{{
                        currentLevel.accounts.length
                            ? 'No accounts match your filters. Try another email or status.'
                            : 'No members yet. Accounts added here will appear in this list.'
                    }}</p>
                </section>

                <section v-if="addPossibleUsersAccess" class="workspace-card" aria-labelledby="suggested-title">
                    <div class="section-heading"
                        ><div
                            ><span class="eyebrow">GROW YOUR TEAM</span
                            ><h2 id="suggested-title"
                                >Suggested accounts <span class="muted">({{ idleUsers.length }})</span></h2
                            ><p
                                >These existing accounts share your organization’s email domain. Add them directly to
                                this organization or one of its branches.</p
                            ></div
                        ></div
                    >
                    <div class="list-controls"
                        ><label class="search-field"
                            >Search suggested accounts<input
                                v-model="search"
                                type="search"
                                placeholder="Search by email" /></label
                        ><span class="result-count" aria-live="polite"
                            >{{ filteredSuggested.length }} results</span
                        ></div
                    >
                    <ul v-if="filteredSuggested.length" class="suggested-list" aria-label="Suggested accounts">
                        <li v-for="item in filteredSuggested" :key="item.id || item.email" class="suggested-row">
                            <span class="person-email">{{ item.email }}</span>
                            <button
                                type="button"
                                class="workspace-button secondary"
                                :aria-label="`Add ${item.email} to an organization`"
                                @click="openSuggested(item)"
                                >Add</button
                            >
                        </li>
                    </ul>
                    <p v-else class="empty-state">No suggested accounts match your search.</p>
                </section>
            </template>

            <v-dialog v-model="accessConfirmation" max-width="460" :persistent="accessBusy">
                <v-card class="organization-dialog" dark elevation="0">
                    <div class="dialog-heading">
                        <h2>{{
                            accessTarget && accessTarget.isAccountLocked
                                ? 'Enable account access?'
                                : 'Disable account access?'
                        }}</h2>
                        <p>{{ accessTarget ? accessTarget.email : '' }}</p>
                    </div>
                    <div class="dialog-body">
                        <p>{{
                            accessTarget && accessTarget.isAccountLocked
                                ? 'This user will regain access to their account.'
                                : 'This will disable access to the user’s account, not just this organization. You can enable it again later.'
                        }}</p>
                    </div>
                    <div class="dialog-actions">
                        <button
                            type="button"
                            class="workspace-button secondary"
                            :disabled="accessBusy"
                            @click="accessConfirmation = false"
                            >Cancel</button
                        >
                        <button
                            type="button"
                            class="workspace-button"
                            :disabled="accessBusy || !accessTarget"
                            @click="userAccountAccess(accessTarget)"
                            >{{
                                accessBusy
                                    ? 'Updating…'
                                    : accessTarget && accessTarget.isAccountLocked
                                    ? 'Enable account'
                                    : 'Disable account'
                            }}</button
                        >
                    </div>
                </v-card>
            </v-dialog>
            <v-dialog
                v-model="addPossibleAccount"
                max-width="520"
                :persistent="addingUser"
                aria-label="Add suggested account"
            >
                <v-card class="organization-dialog" dark elevation="0">
                    <div class="dialog-heading"
                        ><span class="eyebrow">CONNECT AN EXISTING ACCOUNT</span><h2>Add to organization</h2
                        ><p>{{ selectedPossible ? selectedPossible.email : 'Choose an account to continue.' }}</p></div
                    >
                    <div class="dialog-body">
                        <v-select
                            v-model="moveToOrg"
                            :items="orgSelections"
                            item-text="organizationName"
                            item-value="orgId"
                            return-object
                            outlined
                            dense
                            label="Target organization"
                            color="#f0784c"
                            :disabled="addingUser"
                        />
                        <v-switch
                            v-model="addAdmin"
                            label="Give administrator privileges"
                            color="#f0784c"
                            :disabled="addingUser"
                            hide-details
                        />
                        <p>Administrators can manage members and organization details.</p>
                    </div>
                    <div class="dialog-actions"
                        ><button
                            class="workspace-button secondary"
                            type="button"
                            :disabled="addingUser"
                            @click="addPossibleAccount = false"
                            >Close</button
                        ><button
                            class="workspace-button"
                            type="button"
                            :disabled="!canAddSuggested"
                            @click="confirmAddUser(selectedPossible)"
                            >{{ addingUser ? 'Adding…' : 'Add account' }}</button
                        ></div
                    >
                </v-card>
            </v-dialog>
            <v-snackbar v-model="snackbar"
                >{{ snackbarMessage
                }}<template v-slot:action="{ attrs }"
                    ><v-btn text v-bind="attrs" color="white" @click="snackbar = false">Close</v-btn></template
                ></v-snackbar
            >
        </div>
    </main>
</template>

<script>
import { navLinks } from '../navigation.js'
import {
    mdiArrowLeft,
    mdiArrowRight,
    mdiChevronRight,
    mdiClose,
    mdiDotsVertical,
    mdiHomeOutline,
    mdiOfficeBuildingOutline
} from '@mdi/js'
import AccountSettingsHeader from '../account_settings_header.vue'
import CreateSubOrg from '../../../components/branches/create_sub_org.vue'
import PrintOrgAddress from '../../../components/branches/print_address.vue'
import UpdateOrg from '../../../components/branches/update_org.vue'
import AddUser from '../../../components/branches/add_user.vue'
import UpdateUser from '../../../components/branches/update_user.vue'
import RemovePending from '../../../components/branches/remove_pending.vue'
import gql from 'graphql-tag'

export default {
    name: 'Branches',
    components: { AccountSettingsHeader, CreateSubOrg, PrintOrgAddress, UpdateOrg, AddUser, UpdateUser, RemovePending },
    metaInfo: { title: 'Your Account', subTitle: 'Organizations' },
    apollo: {
        queryIdleUsersWithSameDomain: {
            query: gql`
                query QueryIdleUsersWithSameDomain {
                    queryIdleUsersWithSameDomain {
                        email
                        id
                    }
                }
            `,
            update(data) {
                this.idleUsers = (data && data.queryIdleUsersWithSameDomain) || []
                return this.idleUsers
            },
            error() {
                this.notify('Suggested accounts could not be loaded. Try refreshing the workspace.')
            }
        },
        resolveAccountOrganization: {
            variables() {
                return { resolveAccountOrganizationId: this.currentLevel.baseAccountId }
            },
            skip() {
                return !this.currentLevel.baseAccountId
            },
            fetchPolicy: 'network-only',
            query: gql`
                query ResolveAccountOrganization($resolveAccountOrganizationId: String) {
                    resolveAccountOrganization(id: $resolveAccountOrganizationId) {
                        accounts {
                            account {
                                email
                            }
                            accountId
                            admin
                            requestedEmail
                            isAccountLocked
                        }
                        address {
                            city
                            country
                            line1
                            line2
                            postalCode
                            state
                        }
                        orgId
                        enterprise
                        parentOrgId
                        parentOrg {
                            orgId
                            organizationName
                        }
                        organizationName
                        createdByAccountId
                        admins {
                            account {
                                email
                                id
                            }
                            accountId
                            admin
                            requestedEmail
                        }
                        childrenOrg {
                            orgId
                            organizationName
                            address {
                                city
                                country
                                line1
                                line2
                                postalCode
                                state
                            }
                        }
                    }
                }
            `,
            update(data) {
                const organization = data && data.resolveAccountOrganization
                this.pageError = ''
                this.noBranches = !organization
                if (!organization) {
                    this.currentLevel = this.currentLevelObj(null)
                    return null
                }
                this.currentLevel = this.currentLevelObj(organization)
                if (!organization.parentOrgId) this.topOrgAdminsArray = organization.admins || []
                return organization
            },
            error() {
                this.loading = false
                this.pageError = 'Please try again. Your organization and permissions have not been changed.'
            }
        }
    },
    data() {
        return {
            links: navLinks,
            mdiArrowLeft,
            mdiArrowRight,
            mdiChevronRight,
            mdiClose,
            mdiDotsVertical,
            mdiHomeOutline,
            mdiOfficeBuildingOutline,
            idleUsers: [],
            searchUserAccounts: '',
            search: '',
            memberStatus: 'All',
            expandedMember: null,
            moveToOrg: null,
            selectedPossible: null,
            addAdmin: false,
            addPossibleAccount: false,
            addingUser: false,
            accessBusy: false,
            accessConfirmation: false,
            accessTarget: null,
            loading: true,
            pageError: '',
            noBranches: false,
            snackbar: false,
            snackbarMessage: '',
            baseLevelId: '',
            topOrgAdminsArray: [],
            currentLevel: {
                baseAccountId: null,
                baseAccountName: '',
                childrenOrg: [],
                parentOrgId: null,
                parentOrgName: null,
                accounts: [],
                admins: [],
                address: null,
                enterprise: false
            }
        }
    },
    computed: {
        canGoToParent() {
            return !!this.currentLevel.parentOrgId && this.baseLevelId !== this.currentLevel.baseAccountId
        },
        memberCounts() {
            const joined = this.currentLevel.accounts.filter((item) => item.account)
            return {
                joined: joined.length,
                pending: this.currentLevel.accounts.length - joined.length,
                disabled: joined.filter((item) => item.isAccountLocked).length
            }
        },
        filteredMembers() {
            const search = this.searchUserAccounts.trim().toLowerCase()
            return this.currentLevel.accounts.filter((item) => {
                const matchesStatus =
                    this.memberStatus === 'All' ||
                    (this.memberStatus === 'Members' && !!item.account) ||
                    (this.memberStatus === 'Pending' && !item.account) ||
                    (this.memberStatus === 'Disabled' && !!item.account && !!item.isAccountLocked)
                return matchesStatus && item.email.toLowerCase().includes(search)
            })
        },
        filteredSuggested() {
            const search = this.search.trim().toLowerCase()
            return this.idleUsers.filter((item) => (item.email || '').toLowerCase().includes(search))
        },
        addPossibleUsersAccess() {
            return this.checkAdmin(this.currentLevel) && !this.currentLevel.parentOrgId && this.idleUsers.length > 0
        },
        orgSelections() {
            return [
                { organizationName: this.currentLevel.baseAccountName, orgId: this.currentLevel.baseAccountId },
                ...this.currentLevel.childrenOrg
            ].filter((org) => org.orgId)
        },
        canAddSuggested() {
            return !!(
                this.addPossibleUsersAccess &&
                !this.addingUser &&
                this.selectedPossible &&
                this.selectedPossible.email &&
                this.moveToOrg &&
                this.orgSelections.some((org) => org.orgId === this.moveToOrg.orgId)
            )
        }
    },
    watch: {
        searchUserAccounts() {
            this.expandedMember = null
        },
        memberStatus() {
            this.expandedMember = null
        },
        currentLevel() {
            this.expandedMember = null
        },
        addPossibleAccount() {
            this.moveToOrg = null
            this.addAdmin = false
        },
        'currentLevel.baseAccountId'() {
            this.resetFilters()
        }
    },
    created() {
        this.loadBaseOrganization()
    },
    methods: {
        async loadBaseOrganization() {
            this.loading = true
            this.pageError = ''
            this.noBranches = false
            try {
                const result = await this.$apollo.query({
                    query: gql`
                        query ResolveBaseAccountOrganization {
                            resolveBaseAccountOrganization {
                                accounts {
                                    account {
                                        email
                                    }
                                    accountId
                                    admin
                                    requestedEmail
                                }
                                parentOrgId
                                orgId
                                organizationName
                                admins {
                                    account {
                                        email
                                        id
                                    }
                                    accountId
                                    admin
                                    requestedEmail
                                }
                            }
                        }
                    `
                })
                const base = result && result.data && result.data.resolveBaseAccountOrganization
                if (base && base.orgId) {
                    this.baseLevelId = base.orgId
                    this.currentLevel.baseAccountId = base.orgId
                } else this.noBranches = true
            } catch (error) {
                this.pageError = 'Please try again or contact support if the problem continues.'
            } finally {
                this.loading = false
            }
        },
        async retryLoad() {
            this.pageError = ''
            if (!this.currentLevel.baseAccountId) return this.loadBaseOrganization()
            await this.refreshData()
        },
        async refreshData() {
            try {
                await Promise.all([
                    this.$apollo.queries.resolveAccountOrganization.refetch(),
                    this.$apollo.queries.queryIdleUsersWithSameDomain.refetch()
                ])
            } catch (error) {
                this.pageError = 'The workspace could not be refreshed. Please try again.'
            }
        },
        notify(message) {
            this.snackbarMessage = message
            this.snackbar = true
        },
        openSuggested(item) {
            if (!this.addPossibleUsersAccess) return
            this.selectedPossible = item
            this.addPossibleAccount = true
        },
        async confirmAddUser(user) {
            if (!this.canAddSuggested || !user || user !== this.selectedPossible) return
            this.addingUser = true
            const target = this.moveToOrg
            try {
                const result = await this.$apollo.mutate({
                    mutation: gql`
                        mutation AddUserToOrganization($admin: Boolean!, $email: String!, $orgId: String!) {
                            addUserToOrganization(admin: $admin, email: $email, orgId: $orgId) {
                                complete
                                error
                                success
                                uid
                            }
                        }
                    `,
                    variables: { admin: this.addAdmin, email: user.email, orgId: target.orgId }
                })
                const outcome = result && result.data && result.data.addUserToOrganization
                this.notify(
                    outcome && outcome.success
                        ? `${user.email} has successfully been added to ${target.organizationName}.`
                        : `Error adding ${user.email} to ${target.organizationName}`
                )
                this.addPossibleAccount = false
                await this.refreshData()
            } catch (error) {
                this.notify('The account could not be added. Please try again.')
            } finally {
                this.addingUser = false
            }
        },
        checkTopOrg() {
            return this.topOrgAdminsArray.some((item) => item.accountId === this.$auth.user.id)
        },
        updateData(status) {
            if (status) this.notify(status)
            return this.refreshData()
        },
        async deletedOrg(obj) {
            try {
                const result = await this.$apollo.queries.resolveAccountOrganization.fetchMore({
                    variables: { resolveAccountOrganizationId: obj.parentId }
                })
                const parent = result && result.data && result.data.resolveAccountOrganization
                if (!parent) throw new Error('Organization unavailable')
                this.changeOrg(parent.orgId)
                this.notify(obj.status)
            } catch (error) {
                this.pageError = 'The parent organization could not be loaded. Please try again.'
            }
        },
        currentLevelObj(data) {
            const organization = data || {}
            return {
                baseAccountId: organization.orgId || null,
                baseAccountName: organization.organizationName || '',
                enterprise: organization.enterprise || false,
                childrenOrg: organization.childrenOrg || [],
                parentOrgId: organization.parentOrgId || null,
                parentOrgName: organization.parentOrg ? organization.parentOrg.organizationName : null,
                accounts: (organization.accounts || []).map((item) => ({
                    ...item,
                    email: String((item.account && item.account.email) || item.requestedEmail || '').trim()
                })),
                admins: organization.admins || [],
                address: organization.address || null
            }
        },
        resetFilters() {
            this.expandedMember = null
            this.searchUserAccounts = ''
            this.search = ''
            this.memberStatus = 'All'
            this.addPossibleAccount = false
            this.selectedPossible = null
            this.moveToOrg = null
            this.addAdmin = false
        },
        changeOrg(org) {
            if (!org || org === this.currentLevel.baseAccountId) return
            this.resetFilters()
            this.pageError = ''
            this.currentLevel.baseAccountId = org
        },
        checkAdmin(level) {
            return !!(level && level.admins && level.admins.some((item) => item.accountId === this.$auth.user.id))
        },
        canManageMember(item) {
            return this.checkAdmin(this.currentLevel) || (this.checkTopOrg() && !!item.account)
        },
        requestAccountAccess(item) {
            if (!this.checkTopOrg() || !item || !item.account || !item.accountId || this.accessBusy) return
            this.accessTarget = item
            this.accessConfirmation = true
        },
        async userAccountAccess(item) {
            if (!this.checkTopOrg() || !item || !item.account || !item.accountId || this.accessBusy) return
            this.accessBusy = true
            try {
                const result = await this.$apollo.mutate({
                    mutation: gql`
                        mutation disableUserAccount($userId: String!) {
                            disableUserAccount(userId: $userId) {
                                success
                                error
                                uid
                                complete
                            }
                        }
                    `,
                    variables: { userId: item.accountId }
                })
                const outcome = result && result.data && result.data.disableUserAccount
                this.notify(
                    outcome && outcome.success
                        ? 'User permissions have changed.'
                        : 'Error in changing user permissions.'
                )
                if (outcome && outcome.success) {
                    this.accessConfirmation = false
                    this.accessTarget = null
                    await this.refreshData()
                }
            } catch (error) {
                this.notify('User permissions could not be changed. Please try again.')
            } finally {
                this.accessBusy = false
            }
        }
    }
}
</script>

<style scoped>
.organizations-page {
    position: relative;
    min-height: 100vh;
    padding: 30px 24px 64px;
    isolation: isolate;
    color: #f2f3f7;
    background: radial-gradient(ellipse at 88% 0, rgba(219, 81, 32, 0.15), transparent 38rem),
        linear-gradient(155deg, #272a32, #191b22 70%);
    font-family: 'Inter Tight', 'Roboto', sans-serif;
    font-size: 13px;
    line-height: 1.6;
}
.organizations-page::before {
    position: absolute;
    z-index: -1;
    inset: 0;
    background-image: url('https://storage.googleapis.com/gusa-assets/army-BG3.png');
    background-position: center;
    background-size: cover;
    filter: grayscale(1);
    opacity: 0.055;
    pointer-events: none;
    content: '';
}
.organizations-workspace {
    position: relative;
    width: 100%;
    max-width: 1260px;
    margin: 0 auto;
}
.workspace-card,
.workspace-notice {
    margin-bottom: 20px;
    padding: 24px;
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 12px;
    background: #282b33;
    box-shadow: 0 12px 30px rgba(0, 0, 0, 0.1);
}
.organization-summary {
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    gap: 18px 24px;
    padding: 20px;
    background: linear-gradient(115deg, rgba(219, 81, 32, 0.07), transparent 65%), #282b33;
}
.organization-summary.with-branches {
    grid-template-columns: minmax(0, 0.9fr) minmax(0, 1.1fr);
}
.organization-identity,
.branch-browser {
    min-width: 0;
}
.organization-title-row {
    display: flex;
    align-items: flex-start;
    flex-wrap: wrap;
    gap: 8px 16px;
}
.organization-title-row h2 {
    min-width: 0;
    flex: 1 1 220px;
    margin-bottom: 0;
}
.organization-title-row__edit {
    flex: 0 0 auto;
    margin-top: 3px;
}
.identity-actions {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 10px;
    margin-top: 8px;
}
.branch-browser {
    padding-left: 24px;
    border-left: 1px solid rgba(255, 255, 255, 0.1);
}
.branch-browser .section-heading {
    align-items: center;
    gap: 12px;
    margin-bottom: 10px;
}
.branch-heading-copy {
    min-width: 0;
}
.branch-heading-copy h2 {
    margin-top: 5px;
}
.branch-browser ::v-deep .branch-trigger,
.organization-title-row__edit ::v-deep .branch-trigger {
    display: inline-flex;
    min-width: 0;
    min-height: 36px;
    align-items: center;
    justify-content: center;
    padding: 0 12px;
    border: 1px solid rgba(240, 120, 76, 0.4) !important;
    border-radius: 7px;
    color: #ffb396 !important;
    background: rgba(219, 81, 32, 0.08) !important;
    box-shadow: none !important;
    font-family: 'Inter Tight', 'Roboto', sans-serif;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0;
    line-height: 1.25;
    text-decoration: none;
    text-transform: none;
    white-space: nowrap;
    transition: color 140ms ease, background-color 140ms ease, border-color 140ms ease, transform 140ms ease;
}
.branch-browser ::v-deep .branch-trigger::before,
.organization-title-row__edit ::v-deep .branch-trigger::before {
    opacity: 0;
}
.branch-browser ::v-deep .branch-trigger--icon,
.organization-title-row__edit ::v-deep .branch-trigger--icon,
.section-heading ::v-deep .branch-trigger--icon {
    width: 36px;
    min-width: 36px !important;
    height: 36px;
    padding: 0 !important;
    border-radius: 9px;
}
.branch-browser ::v-deep .branch-trigger--icon .v-icon,
.organization-title-row__edit ::v-deep .branch-trigger--icon .v-icon,
.section-heading ::v-deep .branch-trigger--icon .v-icon {
    color: #ffb396;
}
.branch-browser ::v-deep .branch-trigger:hover:not(:disabled),
.organization-title-row__edit ::v-deep .branch-trigger:hover:not(:disabled) {
    border-color: #f0784c !important;
    color: #fff !important;
    background: rgba(219, 81, 32, 0.19) !important;
    transform: translateY(-1px);
}
.branch-browser ::v-deep .branch-trigger:focus-visible,
.organization-title-row__edit ::v-deep .branch-trigger:focus-visible {
    outline: 2px solid #f0784c;
    outline-offset: 3px;
}
.branch-browser h2 {
    margin: 0;
    font-size: 16px;
}
.section-heading,
.workspace-notice {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 20px;
}
.section-heading {
    margin-bottom: 20px;
}
.section-heading > :first-child {
    min-width: 0;
}
.section-heading > :last-child:not(:first-child) {
    flex-shrink: 0;
}
.organizations-page h2,
.organization-dialog h2 {
    margin: 5px 0 8px;
    color: #f5f6fa;
    font-size: 22px;
    font-weight: 600;
    line-height: 1.25;
    overflow-wrap: anywhere;
}
.organizations-page h3 {
    margin: 12px 0 8px;
    font-size: 16px;
    font-weight: 600;
    overflow-wrap: anywhere;
}
.organizations-page p,
.organization-dialog p {
    margin: 0;
    color: #b5bdcc;
    font-size: 12px;
}
.eyebrow {
    color: #f0784c;
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 1.4px;
}
.muted,
.organization-address {
    color: #b5bdcc;
}
.organization-address {
    margin: 8px 0 12px;
    font-size: 12px;
    overflow-wrap: anywhere;
}
.organization-navigation {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 6px 12px;
    grid-column: 1 / -1;
    width: fit-content;
    max-width: 100%;
    margin-bottom: 2px;
}
.organization-navigation__label {
    flex: 0 0 100%;
    color: #9099a9;
    font-size: 9px;
    font-weight: 700;
    letter-spacing: 1.2px;
}
.organization-navigation__trail {
    display: inline-flex;
    max-width: 100%;
    align-items: center;
    gap: 5px;
    padding: 4px;
    border: 1px solid rgba(255, 255, 255, 0.09);
    border-radius: 9px;
    background: #22252d;
}
.organization-navigation__link {
    display: inline-flex;
    min-height: 38px;
    min-width: 0;
    align-items: center;
    gap: 8px;
    padding: 5px 10px;
    border: 1px solid transparent;
    border-radius: 6px;
    color: #e8ebf2;
    background: transparent;
    font-family: inherit;
    font-size: 12px;
    font-weight: 600;
    line-height: 1.25;
    text-align: left;
    cursor: pointer;
    transition: background-color 140ms ease, border-color 140ms ease, color 140ms ease;
}
.organization-navigation__link > .v-icon {
    flex: 0 0 auto;
    color: #ff9a74;
}
.organization-navigation__link > span {
    display: flex;
    min-width: 0;
    flex-direction: column;
    gap: 2px;
    overflow-wrap: anywhere;
}
.organization-navigation__link small {
    color: #aeb5c3;
    font-size: 8px;
    font-weight: 700;
    letter-spacing: 0.8px;
}
.organization-navigation__link:hover {
    color: #fff;
    border-color: rgba(240, 120, 76, 0.24);
    background: rgba(219, 81, 32, 0.12);
}
.organization-navigation__link:focus-visible {
    outline: 2px solid #f0784c;
    outline-offset: 2px;
}
.organization-navigation__link--home {
    color: #ffb396;
}
.organization-navigation__separator {
    display: inline-flex;
    align-items: center;
    color: #717988;
}
.organization-navigation__separator .v-icon {
    color: inherit;
}
.organization-counts {
    display: inline-flex;
    flex-wrap: wrap;
    gap: 8px 0;
    margin: 16px 0 0;
}
.organization-counts > div {
    display: flex;
    align-items: baseline;
    gap: 5px;
    padding: 0 12px;
    border-left: 1px solid #53505a;
}
.organization-counts > div:first-child {
    padding-left: 0;
    border-left: 0;
}
.organization-counts dt {
    color: #b5bdcc;
    font-size: 11px;
}
.organization-counts dd {
    order: -1;
    margin: 0;
    color: #fff;
    font-size: 14px;
    font-weight: 600;
}
.workspace-badge {
    display: inline-block;
    padding: 3px 9px;
    border: 1px solid #535a69;
    border-radius: 6px;
    color: #d1d7e1;
    background: #22252d;
    font-size: 11px;
    white-space: nowrap;
}
.workspace-badge.accent {
    border-color: rgba(240, 120, 76, 0.4);
    color: #ffa781;
    background: rgba(219, 81, 32, 0.1);
}
.workspace-badge.warning {
    border-color: #716144;
    color: #edcc94;
}
.organizations-page .branch-list,
.organizations-page .people-list,
.organizations-page .suggested-list {
    margin: 0;
    padding: 0;
    list-style: none;
}
.branch-list {
    max-height: 224px;
    overflow-y: auto;
    scrollbar-color: #725043 #22252d;
    scrollbar-width: thin;
}
.branch-row {
    overflow: hidden;
    border: 1px solid rgba(255, 255, 255, 0.08);
    border-radius: 8px;
    background: rgba(34, 37, 45, 0.72);
}
.branch-row + .branch-row {
    margin-top: 7px;
}
.branch-details {
    display: flex;
    min-width: 0;
    flex: 1 1 auto;
    flex-direction: column;
    gap: 3px;
    text-align: left;
}
.branch-row__open {
    display: flex;
    width: 100%;
    min-height: 62px;
    align-items: center;
    gap: 12px;
    padding: 10px 13px;
    border: 0;
    color: #f2f3f7;
    background: transparent;
    font: inherit;
    text-align: left;
    cursor: pointer;
    transition: background-color 140ms ease;
}
.branch-row__open:hover {
    background: rgba(219, 81, 32, 0.09);
}
.branch-row__open:focus-visible {
    position: relative;
    outline: 2px solid #f0784c;
    outline-offset: -3px;
}
.branch-details strong {
    color: #f2f3f7;
    font-size: 13px;
    font-weight: 600;
    line-height: 1.4;
    overflow-wrap: anywhere;
}
.branch-row__open > .v-icon {
    flex: 0 0 auto;
    color: #ff9a74;
    transition: transform 140ms ease;
}
.branch-row__open:hover > .v-icon {
    transform: translateX(3px);
}
.branch-row__action {
    flex: 0 0 auto;
    padding: 5px 8px;
    border: 1px solid rgba(240, 120, 76, 0.24);
    border-radius: 6px;
    color: #ffb396;
    font-size: 10px;
    font-weight: 600;
    white-space: nowrap;
}
.branch-row .organization-address {
    margin: 0;
    font-size: 11px;
}
.branch-create-action {
    display: flex;
    flex: 0 0 auto;
    align-items: center;
}
.branch-empty {
    padding: 10px 0;
}
.list-controls {
    display: flex;
    align-items: flex-end;
    flex-wrap: wrap;
    gap: 14px;
    margin: 0 0 18px;
}
.search-field,
.status-field {
    display: flex;
    flex-direction: column;
    gap: 6px;
    color: #c9cfd9;
    font-size: 11px;
}
.search-field {
    flex: 1 1 260px;
    max-width: 420px;
}
.status-field {
    flex: 0 1 160px;
}
.search-field input,
.status-field select {
    width: 100%;
    min-height: 40px;
    padding: 8px 12px;
    border: 1px solid #59606f;
    border-radius: 8px;
    color: #f2f3f7;
    background: #22252d;
    font: inherit;
    font-size: 12px;
    color-scheme: dark;
}
.search-field input::placeholder {
    color: #a2aaba;
}
.result-count {
    align-self: center;
    margin-top: 20px;
    color: #b5bdcc;
    font-size: 11px;
}
.people-list,
.suggested-list {
    border: 1px solid rgba(255, 255, 255, 0.09);
    border-radius: 10px;
}
.person-row + .person-row,
.suggested-row + .suggested-row {
    border-top: 1px solid rgba(255, 255, 255, 0.08);
}
.person-summary,
.suggested-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 12px 14px;
}
.person-identity {
    display: flex;
    min-width: 0;
    align-items: center;
    flex-wrap: wrap;
    gap: 6px 16px;
}
.person-email {
    min-width: 0;
    color: #f2f3f7;
    font-size: 12px;
    font-weight: 500;
    overflow-wrap: anywhere;
}
.person-summary:hover,
.suggested-row:hover {
    background: rgba(255, 255, 255, 0.025);
}
.person-badges {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
}
.person-controls {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    flex-wrap: wrap;
    gap: 10px;
    flex-shrink: 0;
    margin-left: auto;
}
.person-badges .workspace-badge {
    padding: 1px 7px;
    font-size: 10px;
}
.person-badges .badge-admin {
    color: #f1f3f7;
    background: #3a3f49;
    border-color: #626b7b;
}
.person-badges .badge-pending {
    color: #d1e8ff;
    background: #24394f;
    border-color: #52789c;
}
.person-badges .badge-disabled {
    color: #ffd6dc;
    background: #492e37;
    border-color: #9a5d6d;
}
.manage-button,
.suggested-row > button,
.branch-row > button {
    flex-shrink: 0;
}
.manage-button {
    display: inline-flex;
    min-width: 34px;
    min-height: 34px;
    flex: 0 0 auto;
    align-items: center;
    justify-content: center;
    gap: 5px;
    padding: 0 4px;
    border: 0;
    border-radius: 5px;
    color: #ffb396 !important;
    background: transparent;
    font: inherit;
    font-size: 11px;
    font-weight: 600;
    cursor: pointer;
    transition: background-color 140ms ease, color 140ms ease;
}
.manage-button:hover,
.manage-button[aria-expanded='true'] {
    color: #fff !important;
    background: rgba(219, 81, 32, 0.12);
}
.manage-button ::v-deep .v-icon {
    color: inherit !important;
    font-size: 18px;
}
.manage-button:focus-visible {
    outline: 2px solid #f0784c;
    outline-offset: 2px;
}
.workspace-button.account-access-button {
    min-height: 32px;
    padding: 5px 8px;
    color: #ffb396 !important;
    border-color: transparent;
    background: transparent;
}
.workspace-button.account-access-button:hover {
    background: rgba(240, 120, 76, 0.08);
    border-color: transparent;
}
.organization-actions ::v-deep .branch-trigger {
    padding: 0 8px;
    color: #ffb396 !important;
    border: 0;
    border-radius: 6px;
    background: transparent;
}
.organization-actions ::v-deep .branch-trigger::before {
    background: #f0784c;
}
.member-actions {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
    gap: 12px;
    margin: 0 14px 12px;
    padding: 12px;
    border: 1px solid rgba(240, 120, 76, 0.25);
    border-left: 3px solid #db5120;
    border-radius: 8px;
    background: #22252d;
}
.member-actions-enter-active,
.member-actions-leave-active {
    overflow: hidden;
    transform-origin: top center;
    transition: opacity 180ms ease, transform 180ms ease, max-height 220ms ease, margin 180ms ease, padding 180ms ease;
}
.member-actions-enter,
.member-actions-leave-to {
    max-height: 0;
    margin-top: 0;
    margin-bottom: 0;
    padding-top: 0;
    padding-bottom: 0;
    opacity: 0;
    transform: translateY(-6px) scaleY(0.96);
}
.member-actions-enter-to,
.member-actions-leave {
    max-height: 240px;
    opacity: 1;
    transform: translateY(0) scaleY(1);
}
.access-actions,
.organization-actions {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    padding: 14px;
    border: 1px solid rgba(255, 255, 255, 0.08);
    border-radius: 7px;
    background: #282b33;
}
.action-copy {
    min-width: 0;
}
.member-actions .action-copy h3 {
    margin: 0 0 4px;
    color: #eef0f5;
    font-size: 12px;
    font-weight: 600;
}
.member-actions .action-copy p {
    max-width: 36ch;
    font-size: 11px;
    line-height: 1.6;
}
.access-actions > button,
.organization-actions > div:last-child:not(.action-copy) {
    flex-shrink: 0;
}
.workspace-button {
    display: inline-flex;
    min-height: 36px;
    align-items: center;
    justify-content: center;
    padding: 7px 14px;
    border: 1px solid #db5120;
    border-radius: 8px;
    color: #fff !important;
    background: #db5120;
    font-family: 'Inter Tight', 'Roboto', sans-serif;
    font-size: 12px;
    font-weight: 600;
    line-height: 1.5;
    text-decoration: none;
    white-space: nowrap;
    cursor: pointer;
}
.workspace-button.secondary {
    border-color: #626a79;
    color: #f1f2f6 !important;
    background: #22252d;
}
.workspace-button:hover {
    border-color: #f0784c;
    background: #b84420;
}
.workspace-button.secondary:hover {
    background: #36313a;
}
.workspace-button:disabled {
    opacity: 0.45;
    cursor: not-allowed;
}
.organizations-page button:focus-visible,
.organizations-page input:focus-visible,
.organizations-page select:focus-visible,
.organizations-page a:focus-visible,
.branch-list:focus-visible,
.organization-dialog button:focus-visible {
    outline: 2px solid #f0784c;
    outline-offset: 3px;
}
.empty-state {
    padding: 28px 16px !important;
    text-align: center;
    white-space: normal !important;
}
.inline-actions {
    display: flex;
    justify-content: center;
    flex-wrap: wrap;
    gap: 10px;
    margin-top: 20px;
}
.organization-dialog {
    overflow: hidden;
    border: 1px solid #55505a !important;
    border-top: 2px solid #db5120 !important;
    border-radius: 12px !important;
    color: #f2f3f7;
    background: #282b33 !important;
    font-family: 'Inter Tight', 'Roboto', sans-serif;
    font-size: 13px;
}
.dialog-heading {
    padding: 24px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}
.dialog-heading p {
    overflow-wrap: anywhere;
}
.dialog-body {
    padding: 24px;
}
.dialog-body p {
    margin-top: 12px;
}
.dialog-body ::v-deep .v-input {
    font-family: inherit;
    font-size: 13px;
}
.dialog-body ::v-deep .v-label {
    font-size: 13px;
}
.dialog-actions {
    display: flex;
    justify-content: space-between;
    gap: 14px;
    padding: 18px 24px;
    background: #22252d;
}
@media (max-width: 800px) {
    .organization-summary.with-branches {
        grid-template-columns: minmax(0, 1fr);
    }
    .branch-browser {
        padding: 16px 0 0;
        border-top: 1px solid rgba(255, 255, 255, 0.1);
        border-left: 0;
    }
}
@media (max-width: 600px) {
    .organizations-page {
        padding: 18px 12px 40px;
    }
    .workspace-card,
    .workspace-notice {
        padding: 18px 14px;
    }
    .section-heading,
    .workspace-notice {
        flex-direction: column;
        gap: 14px;
    }
    .organizations-page h2,
    .organization-dialog h2 {
        font-size: 20px;
    }
    .branch-browser .section-heading {
        flex-direction: row;
    }
    .person-identity {
        align-items: flex-start;
        flex-direction: column;
    }
    .person-summary {
        flex-wrap: wrap;
    }
    .person-controls {
        max-width: 100%;
    }
    .member-actions {
        grid-template-columns: minmax(0, 1fr);
    }
    .member-actions-enter-to,
    .member-actions-leave {
        max-height: 360px;
    }
    .access-actions,
    .organization-actions {
        flex-wrap: wrap;
    }
    .search-field {
        max-width: none;
        flex-basis: 100%;
    }
    .status-field {
        flex: 1;
    }
    .result-count {
        margin-left: auto;
    }
    .dialog-heading,
    .dialog-body {
        padding: 20px;
    }
}
@media (prefers-reduced-motion: reduce) {
    .manage-button,
    .member-actions-enter-active,
    .member-actions-leave-active {
        transition: none;
    }
}
</style>
